import { FC, c } from 'lib/component-utils'
import { useUserContext } from 'lib/context'
import { useState } from 'react'
import Card from 'components/base/Card'
import { ArrowRightIcon, XIcon } from '@heroicons/react/outline'
import { useIntl } from 'lib/intl-utils'
import { useEffectOnUpdate, useStateFromLocalStorage } from 'lib/hooks'
import dayjs from 'dayjs'
import { Changelog } from 'lib/strapi'
import { useRouter } from 'next/router'
import Time from 'components/base/Time'

const ChangelogNotifier: FC = () => {
  const ctx = useUserContext()
  const { t } = useIntl()
  const [isShown, setIsShown] = useState(false)
  const [changelogState, setChangelogState] = useStateFromLocalStorage('changelogState', {
    lastClosed: null,
    lastOpened: null,
    lastChecked: null,
    lastCheckLatestEntryDate: null,
  })
  const [entries, setEntries] = useState<Changelog[]>([])
  const [checkedThisTime, setCheckedThisTime] = useState(false)
  const router = useRouter()

  useEffectOnUpdate(() => {
    if (changelogState === undefined) return
    if (!changelogState.lastClosed) {
      setChangelogState((state) => ({
        ...state,
        lastClosed: dayjs().subtract(3, 'day').toISOString(),
      }))
    }
    if (!changelogState.lastOpened) {
      setChangelogState((state) => ({
        ...state,
        lastOpened: dayjs().subtract(3, 'day').toISOString(),
      }))
    }
  }, [changelogState])

  useEffectOnUpdate(() => {
    if (changelogState === undefined) return
    if (
      (ctx.theme.hostType === 'sellmonitor' || ctx.theme.hostType === 'sellematics') &&
      (!router.asPath.includes('subscription') || router.asPath.includes('settings')) &&
      !router.asPath.includes('trial/welcome') &&
      (!changelogState.lastChecked ||
        dayjs().diff(changelogState.lastChecked, 'minutes') > 60 ||
        (dayjs(changelogState.lastCheckLatestEntryDate).isAfter(changelogState.lastClosed) &&
          !checkedThisTime))
    ) {
      ctx.getChangelogEntries(1, []).then(({ items }) => {
        if (
          !changelogState.lastClosed ||
          dayjs(changelogState.lastClosed).isBefore(dayjs(items[0].changeDate))
        ) {
          setIsShown(true)
          setEntries(items)
          setChangelogState((state) => ({
            ...state,
            lastCheckLatestEntryDate: items[0].changeDate,
          }))
        } else {
          setChangelogState((state) => ({
            ...state,
            lastCheckLatestEntryDate: items[0].changeDate,
          }))
        }
      })
      setChangelogState((state) => ({
        ...state,
        lastChecked: dayjs().toISOString(),
      }))
      setCheckedThisTime(true)
    }
  }, [changelogState])

  const latest = entries[0]

  if (!isShown || !latest) {
    return null
  }

  const markOpened = () => {
    setIsShown(false)
    setChangelogState((state) => ({
      ...state,
      lastClosed: dayjs().toISOString(),
      lastOpened: dayjs().toISOString(),
    }))
  }

  const image = getFirstImage(latest)
  const description = getDescription(latest)
  const previous = entries.slice(1, 4)

  return (
    <Card className="w-[24rem] max-w-full max-h-[calc(100dvh-3rem)] overflow-y-auto !shadow-2xl ring-gray-300 animate-scale-in origin-bottom-left">
      <div className="flex items-center justify-between pl-5 pr-3 pt-3 pb-2">
        <h3 className="flex items-center gap-2 text-sm font-medium text-gray-600">
          <span className="relative flex size-2">
            <span className="absolute inset-0 rounded-full bg-green-500 opacity-75 animate-ping" />
            <span className="relative size-2 rounded-full bg-green-500" />
          </span>
          {t`changelog_whats_new`}
        </h3>
        <button
          aria-label={t`close`}
          className="relative flex items-center justify-center size-7 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 before:absolute before:-inset-2"
          onClick={() => {
            setIsShown(false)
            setChangelogState((state) => ({
              ...state,
              lastClosed: dayjs().toISOString(),
            }))
          }}
        >
          <XIcon className="size-4" />
        </button>
      </div>
      <a href={`/help/changelog/#${latest.id}`} className="group block px-5" onClick={markOpened}>
        {image && (
          <div className="aspect-[2/1] overflow-hidden rounded-md ring-1 ring-gray-200 bg-gray-50">
            <img
              src={image.url}
              alt=""
              style={{ width: image.width ? image.width / 2 : undefined }}
              className="max-w-none min-w-full min-h-full object-cover object-left-top"
            />
          </div>
        )}
        <Time
          dateTime={latest.changeDate ?? latest.createdAt}
          options={{ day: 'numeric', month: 'long' }}
          className={c`block text-xs text-gray-500 ${!!image} mt-3`}
        />
        <p className="mt-0.5 text-base font-medium text-pretty group-hover:text-primary-600">
          {latest.title}
        </p>
        {description && <p className="mt-1 text-sm text-gray-600 line-clamp-3">{description}</p>}
      </a>
      <div className="px-5 pt-5 pb-4">
        {previous.length > 0 && (
          <h4 className="mb-2 text-xs font-medium text-gray-500">{t`changelog_earlier`}</h4>
        )}
        <ul className="flex flex-col">
          {previous.map((item) => (
            <li key={item.id} className="relative">
              <span className="absolute left-0 top-[0.4375rem] size-[7px] rounded-full bg-gray-400" />
              <span className="absolute left-[3px] top-[0.875rem] -bottom-[0.4375rem] border-l border-dashed border-gray-400" />
              <a
                href={`/help/changelog/#${item.id}`}
                className="group block pl-5 pb-2.5 text-sm text-pretty"
                onClick={markOpened}
              >
                <span className="text-gray-800 group-hover:text-primary-600">{item.title}</span>
                <Time
                  dateTime={item.changeDate ?? item.createdAt}
                  options={{ day: 'numeric', month: 'short' }}
                  className="ml-1.5 text-xs text-gray-500 whitespace-nowrap"
                />
              </a>
            </li>
          ))}
          <li className="relative">
            <span className="absolute left-0 top-[0.4375rem] size-[7px] rounded-full bg-gray-400" />
            <a
              href="/help/changelog/"
              className="group flex items-center gap-1 pl-5 text-sm font-medium text-gray-800 hover:text-primary-600"
              onClick={markOpened}
            >
              {t`changelog_all_changes`}
              <ArrowRightIcon className="size-3.5 text-gray-500 group-hover:text-primary-600" />
            </a>
          </li>
        </ul>
      </div>
    </Card>
  )
}

const getFirstImage = (entry: Changelog): { url: string; width: number | null } | null => {
  const block = entry.content?.blocks?.find((block) => block.type === 'image')
  const file = block?.data?.file
  return file?.url ? { url: file.url, width: file.width ?? null } : null
}

const getDescription = (entry: Changelog): string => {
  const html = (entry.content?.blocks ?? [])
    .filter((block) => block.type === 'paragraph')
    .slice(0, 3)
    .map((block) => block.data?.text ?? '')
    .join(' ')
  return new DOMParser().parseFromString(html, 'text/html').body.textContent?.trim() ?? ''
}

export default ChangelogNotifier
